import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';

const CookiePolicyPage = () => {
  useEffect(() => {
    document.title = "Cookie Policy | Forwardsflow";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Forwardsflow Cookie Policy — how we use cookies and similar tracking technologies, what types of cookies we use, and how to manage your cookie preferences.");
    }
  }, []);

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased flex flex-col justify-between">
      <div>
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between h-20">
              <Logo />
              <div className="hidden lg:flex items-center gap-6">
                <Link to="/products" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Platform</Link>
                <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
                <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
                <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
                <Link to="/csr" className="font-mono text-xs uppercase tracking-[0.15em] text-green-600 hover:text-green-700 transition-colors">CSR</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Header Banner */}
        <div className="pt-32 pb-16 bg-[#4D4D4D] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <div className="w-12 h-12 border border-zinc-400 bg-white/10 rounded-none flex items-center justify-center mx-auto mb-6">
              <Cookie className="w-6 h-6 text-white" />
            </div>
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 block mb-2">Legal &amp; Compliance</span>
            <h1 className="font-sans text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">Cookie Policy</h1>
            <p className="font-sans text-base text-zinc-300 max-w-xl mx-auto">
              How we use cookies and similar technologies on the Forwardsflow platform.
            </p>
            <p className="font-mono text-xs text-zinc-400 mt-4">Effective date: 1 September 2026 &nbsp;|&nbsp; Last updated: 11 September 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <div className="bg-white border border-zinc-300 p-8 md:p-12 rounded-none space-y-10">

            {/* 1. What Are Cookies */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 01</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">1. What Are Cookies</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and supply information to the website operator.
              </p>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed">
                This Cookie Policy explains what cookies we use, why we use them, and how you can control them. This policy should be read alongside our <Link to="/privacy" className="text-black font-bold underline">Privacy Policy</Link>, which provides further detail on how we collect and process personal data.
              </p>
            </section>

            {/* 2. Who Sets Cookies */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 02</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">2. Who Sets Cookies on This Site</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                Cookies on our Platform are set by:
              </p>
              <ul className="list-disc list-inside font-sans text-sm text-zinc-700 space-y-2">
                <li><strong>Forwardsflow (first-party cookies):</strong> Set directly by our Platform for authentication, session management, and user preferences.</li>
                <li><strong>Third-party services:</strong> Set by services we use for analytics and performance monitoring, including Google Analytics.</li>
              </ul>
            </section>

            {/* 3. Types of Cookies We Use */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 03</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">3. Types of Cookies We Use</h2>

              <h3 className="font-sans text-base font-bold text-black mb-2 mt-6">3.1 Strictly Necessary Cookies</h3>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                These cookies are essential for the Platform to function and cannot be switched off. They are usually set in response to actions you take, such as logging in, setting your privacy preferences, or filling in forms. Without these cookies, the Platform cannot operate correctly.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm font-sans border border-zinc-300">
                  <thead>
                    <tr className="bg-zinc-100">
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Cookie</th>
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Purpose</th>
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-700">
                    <tr><td className="p-3 border-b border-zinc-200">ff_session</td><td className="p-3 border-b border-zinc-200">User authentication and session management</td><td className="p-3 border-b border-zinc-200">Session</td></tr>
                    <tr><td className="p-3 border-b border-zinc-200">ff_cookie_consent</td><td className="p-3 border-b border-zinc-200">Stores your cookie consent preference</td><td className="p-3 border-b border-zinc-200">1 year</td></tr>
                    <tr><td className="p-3 border-b border-zinc-200">ff_csrf</td><td className="p-3 border-b border-zinc-200">Cross-site request forgery protection</td><td className="p-3 border-b border-zinc-200">Session</td></tr>
                  </tbody>
                </table>
              </div>

              <h3 className="font-sans text-base font-bold text-black mb-2">3.2 Analytics Cookies</h3>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                These cookies help us understand how visitors interact with the Platform by collecting and reporting information anonymously. They allow us to measure traffic, identify popular pages, and improve the user experience. These cookies are set only with your consent.
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm font-sans border border-zinc-300">
                  <thead>
                    <tr className="bg-zinc-100">
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Cookie</th>
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Purpose</th>
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Duration</th>
                      <th className="text-left p-3 border-b border-zinc-300 font-bold text-black">Provider</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-700">
                    <tr><td className="p-3 border-b border-zinc-200">_ga</td><td className="p-3 border-b border-zinc-200">Distinguishes unique users</td><td className="p-3 border-b border-zinc-200">2 years</td><td className="p-3 border-b border-zinc-200">Google Analytics</td></tr>
                    <tr><td className="p-3 border-b border-zinc-200">_ga_*</td><td className="p-3 border-b border-zinc-200">Maintains session state</td><td className="p-3 border-b border-zinc-200">2 years</td><td className="p-3 border-b border-zinc-200">Google Analytics</td></tr>
                    <tr><td className="p-3 border-b border-zinc-200">_gid</td><td className="p-3 border-b border-zinc-200">Distinguishes unique users</td><td className="p-3 border-b border-zinc-200">24 hours</td><td className="p-3 border-b border-zinc-200">Google Analytics</td></tr>
                  </tbody>
                </table>
              </div>

              <h3 className="font-sans text-base font-bold text-black mb-2">3.3 Functional Cookies</h3>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed">
                We may use functional cookies to remember your preferences, such as language selection, display settings, or previously viewed markets. These cookies enhance your experience but are not essential for the Platform to operate.
              </p>
            </section>

            {/* 4. How to Manage Cookies */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 04</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">4. How to Manage Cookies</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                When you first visit our Platform, you will be presented with a cookie consent banner allowing you to accept or decline non-essential cookies.
              </p>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                You can also control cookies through your browser settings. Most browsers allow you to:
              </p>
              <ul className="list-disc list-inside font-sans text-sm text-zinc-700 space-y-2 mb-4">
                <li>View what cookies are stored and delete them individually</li>
                <li>Block third-party cookies</li>
                <li>Block cookies from specific websites</li>
                <li>Block all cookies from being set</li>
                <li>Delete all cookies when you close your browser</li>
              </ul>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed">
                Please note that blocking or deleting strictly necessary cookies may impair the functionality of the Platform, including your ability to log in and access your account.
              </p>
            </section>

            {/* 5. Similar Technologies */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 05</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">5. Similar Technologies</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                In addition to cookies, we may use similar technologies including:
              </p>
              <ul className="list-disc list-inside font-sans text-sm text-zinc-700 space-y-2">
                <li><strong>Local storage:</strong> Used to store your cookie consent preference and UI settings on your device.</li>
                <li><strong>Session storage:</strong> Used to maintain state during your browsing session (e.g., form data in progress).</li>
              </ul>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mt-4">
                These technologies are subject to the same controls and consent requirements as cookies.
              </p>
            </section>

            {/* 6. Legal Basis */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 06</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">6. Legal Basis for Using Cookies</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                We use strictly necessary cookies on the basis of our legitimate interest in providing a secure and functional Platform. For all other cookies (analytics and functional), we rely on your consent, which you can give or withdraw at any time via the cookie consent banner or your browser settings.
              </p>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed">
                This approach is in accordance with the UK Privacy and Electronic Communications Regulations (PECR), the UK GDPR, the UAE Federal Decree-Law No. 45 of 2021 (PDPL), and the DIFC Data Protection Law.
              </p>
            </section>

            {/* 7. Changes */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 07</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">7. Changes to This Cookie Policy</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in the cookies we use, changes in technology, or changes in applicable law. The "Last updated" date at the top of this page indicates when the policy was last revised. We encourage you to review this policy periodically.
              </p>
            </section>

            {/* 8. Contact */}
            <section>
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-2">Section 08</span>
              <h2 className="font-sans text-2xl font-bold text-black mb-4">8. Contact Us</h2>
              <p className="font-sans text-sm text-zinc-700 leading-relaxed mb-4">
                If you have any questions about this Cookie Policy or our use of cookies, please contact us:
              </p>
              <div className="bg-zinc-50 border border-zinc-300 p-6 rounded-none font-mono text-xs">
                <p className="font-bold text-black uppercase tracking-wider mb-2">Data Protection Officer</p>
                <p className="text-zinc-600">Forwards Flow Inc.</p>
                <p className="text-zinc-600">2055 Limestone Rd STE 200-C, Wilmington, DE 19808, United States</p>
                <p className="text-zinc-600 mt-2">Email: <a href="mailto:privacy@forwardsflow.com" className="underline">privacy@forwardsflow.com</a></p>
              </div>
            </section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
